import { avatarDefault } from '@/assets/images';
import { Divider, HorizontalScrollView, Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui';
import { Image } from '@/components/ui/image';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { ContentWrapper } from '@/features/player-session/components/session-summary/content-wrapper';
import {
  collapsibleOpenCache,
  scrollPageCache,
  summaryResetStore,
} from '@/features/player-session/components/session-summary/summary-card-cache-state';
import { PlayerDetailsSections } from '@/features/player-session/components/session-summary/summary-card-detail';
import { buildDetailsByLevel, getPinsDisplay } from '@/features/player-session/utils';
import { PlayerCardAvatar } from '@/features/player/components/list-player/player-card/player-card-avatar';
import { PlayerCardDesc } from '@/features/player/components/list-player/player-card/player-card-desc';
import {
  PlayerCardIndicator,
  PlayerCardRoot,
} from '@/features/player/components/list-player/player-card/player-card-root';
import { PlayerCardTitle } from '@/features/player/components/list-player/player-card/player-card-title';
import { buildPlayerAvatarSource } from '@/features/player/utils/avatar-uri';
import { createStyles, useScaling } from '@/hooks';
import { HomeStackParamList } from '@/navigations/types';
import { colors, ColorToken } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, {
  Activity,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  type ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

const MOBILE_PIN_NUMBER_PER_PAGE = 4;
const TABLET_PIN_NUMBER_PER_PAGE = 6;

const DETAILS_LABEL_STYLE = { color: colors.Blue } as const;

export const usePinsLayoutCalc = (allPins: playerSession.Pin[]) => {
  const { scale, isTablet } = useScaling();
  const pinSize = scale(54);
  const pins = useMemo(() => getPinsDisplay(allPins ?? []), [allPins]);
  const pinGap = scale(12);
  const pinPadding = scale(6);
  const PIN_NUMBER_PER_PAGE = isTablet ? TABLET_PIN_NUMBER_PER_PAGE : MOBILE_PIN_NUMBER_PER_PAGE;
  const hasOverflow = pins.length > PIN_NUMBER_PER_PAGE;
  const overflowWidth =
    pinPadding + (PIN_NUMBER_PER_PAGE + 0.5) * pinSize + PIN_NUMBER_PER_PAGE * pinGap;
  const contentWidth = pinPadding + pins.length * pinSize + Math.max(0, pins.length - 1) * pinGap;
  const visibleWidth = hasOverflow ? overflowWidth : contentWidth;
  return {
    visibleWidth,
    contentWidth,
    overflowWidth,
    pins,
    hasOverflow,
    pinGap,
    pinPadding,
    pinSize,
  };
};

export const PlayerSummaryCard = memo(
  ({ player }: { player: playerSession.SessionLookupData }) => {
    const { scale, isTablet } = useScaling();
    const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();
    const avatarSize = scale(40);
    const styles = useStyles();
    const pinSize = scale(54);

    const avatarSource = useMemo(
      () =>
        buildPlayerAvatarSource({
          playerId: player.id,
          avatarUrl: player.avatar_url,
        }) ?? avatarDefault,
      [player.id, player.avatar_url],
    );

    const onPress = useCallback(() => {
      navigation.navigate('PlayerProfile', {
        playerId: player.id,
        mutateDeleteReturnTo: 'OverviewSession',
      });
    }, [navigation, player.id]);

    const detailsByLevel = useMemo(() => buildDetailsByLevel(player), [player]);

    const {
      visibleWidth,
      contentWidth,
      pins: mainPins,
      pinGap,
      pinPadding,
      hasOverflow,
    } = usePinsLayoutCalc(player.pins ?? []);

    const cachedOffset = scrollPageCache.get(player.id) ?? 0;
    const scrollRef = useRef<ScrollView>(null);

    const onScrollEnd = useCallback(
      (e: NativeSyntheticEvent<NativeScrollEvent>) => {
        scrollPageCache.set(player.id, e.nativeEvent.contentOffset.x);
      },
      [player.id],
    );

    const resetVersion = useSyncExternalStore(
      summaryResetStore.subscribe,
      summaryResetStore.getSnapshot,
    );

    const initialOpen = collapsibleOpenCache.get(player.id) ?? false;
    const [collapsibleOpen, setCollapsibleOpen] = useState(initialOpen);

    useEffect(() => {
      setCollapsibleOpen(collapsibleOpenCache.get(player.id) ?? false);
    }, [player.id, resetVersion]);

    const onTriggerPress = useCallback(() => {
      setCollapsibleOpen(prev => {
        const next = !prev;
        collapsibleOpenCache.set(player.id, next);
        return next;
      });
    }, [player.id]);

    const scrollContentStyle = useMemo(() => ({ alignItems: 'center' as const }), []);
    const scrollStyle: ViewStyle = useMemo(
      () => ({
        width: visibleWidth,
        maxWidth: '100%',
        height: pinSize,
      }),
      [visibleWidth, pinSize],
    );
    const innerRowStyle: ViewStyle = useMemo(() => ({ width: contentWidth }), [contentWidth]);
    const detailsOuterStyle: ViewStyle | undefined = useMemo(() => {
      if (Platform.OS === 'android' && isTablet) {
        return { marginTop: -StyleSheet.hairlineWidth };
      }
      return undefined;
    }, [isTablet]);

    return (
      <Stack paddingX={scale({ phone: 18, tablet: 0 })}>
        <Stack width={'100%'} style={styles.playerSessionCard} borderRadius={2}>
          <PlayerCardRoot
            onPress={onPress}
            hideShadow
            borderRadius={0}
            borderTopLeftRadius={2}
            borderTopRightRadius={2}
            overflow="hidden"
            height={scale(49)}
            marginX={0}>
            <PlayerCardIndicator color={colors[`Level ${player.level}` as ColorToken]} />
            <PlayerCardAvatar
              size={avatarSize}
              source={avatarSource}
              onPress={onPress}
              hideProfileLabel={true}
            />

            <Stack flex={1} justify="center" gap={scale(8)}>
              <Stack gap={scale(2)}>
                <PlayerCardTitle
                  title={player.name.replace(STRINGS.player.listPlayers.creatingPlayerMarker, '')}
                />
                <PlayerCardDesc>Level {`${player.level}`}</PlayerCardDesc>
              </Stack>
            </Stack>
          </PlayerCardRoot>
          <Divider thickness={1} color={colors['3']} />
          <View
            style={collapsibleOpen ? styles.hidden : undefined}
            pointerEvents={collapsibleOpen ? 'none' : 'auto'}>
            <ContentWrapper level={player.level}>
              <Activity mode={'visible'}>
                <Stack height={scale(80)} justify="center" align="center">
                  {mainPins.length ? (
                    <HorizontalScrollView
                      key={`${player.id}-${resetVersion}`}
                      ref={scrollRef}
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      onMomentumScrollEnd={onScrollEnd}
                      bounces={false}
                      contentOffset={{ x: cachedOffset, y: 0 }}
                      snapToInterval={pinSize + pinGap}
                      contentContainerStyle={scrollContentStyle}
                      style={scrollStyle}>
                      <Stack
                        direction="row"
                        paddingLeft={hasOverflow ? pinPadding : pinPadding / 2}
                        gapX={pinGap}
                        height={pinSize}
                        style={innerRowStyle}>
                        {mainPins.map((pin, idx) => (
                          <Image
                            key={`${pin.imageUrl ?? 'pin'}-${idx}-${pin.name}`}
                            source={{ uri: pin.imageUrl ?? '' }}
                            persistLoadedUri
                            style={[styles.pinImage, { width: pinSize, height: pinSize }]}
                          />
                        ))}
                      </Stack>
                    </HorizontalScrollView>
                  ) : (
                    <Text variant="Font Inter/Subtitle/Regular 12px" style={styles.noPinsText}>
                      {STRINGS.playerSession.sessionSummary.noPinsMessage}
                    </Text>
                  )}
                </Stack>
              </Activity>
            </ContentWrapper>
            <Divider thickness={1} color={colors['3']} />
          </View>
          <TouchableOpacity
            onPress={onTriggerPress}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityState={{ expanded: collapsibleOpen }}>
            <ContentWrapper level={player.level}>
              <Stack
                direction="row"
                align="center"
                justify="space-between"
                gap={scale(4)}
                paddingY={scale(14)}>
                <Text
                  variant="Font Montserrat/Subtitle/Minimize 2 Regular 10px"
                  style={DETAILS_LABEL_STYLE}>
                  {collapsibleOpen
                    ? STRINGS.playerSession.sessionSummary.overview
                    : STRINGS.playerSession.sessionSummary.details}
                </Text>
                <SvgIcon
                  name="IcBack"
                  transform={[{ rotate: collapsibleOpen ? '90deg' : '-90deg' }]}
                  size={scale(14)}
                  color={'#E97D27'}
                />
              </Stack>
            </ContentWrapper>
          </TouchableOpacity>
          <View
            style={[detailsOuterStyle, collapsibleOpen ? undefined : styles.hidden]}
            pointerEvents={collapsibleOpen ? 'auto' : 'none'}>
            <PlayerDetailsSections detailsByLevel={detailsByLevel} level={player.level} />
          </View>
        </Stack>
      </Stack>
    );
  },
  (prev, next) => {
    return (
      prev.player.id === next.player.id &&
      prev.player.name === next.player.name &&
      prev.player.level === next.player.level &&
      prev.player.attempts === next.player.attempts &&
      prev.player.pins === next.player.pins &&
      prev.player.avatar_url === next.player.avatar_url
    );
  },
);

const useStyles = createStyles(() => ({
  playerSessionCard: {
    backgroundColor: 'white',
    borderRadius: 2,
    overflow: 'hidden',
    ...(Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
      },
      android: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
      },
    }) ?? {}),
  },
  noPinsText: {
    color: colors['5'],
    textAlign: 'center',
    width: '100%',
  },
  pinImage: {},
  contentWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: colors['3'],
  },
  hidden: {
    height: 0,
    overflow: 'hidden',
  },
}));
